import { Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FileComponent } from '@app/shared/forms';
import { IconComponent } from '@app/shared/components';
import { AssessmentUploadStatusService } from '@app/shared/services';
import { CloseComponent } from '../shared/components';
import { ASSESSMENT_UPLOAD_TYPE, AssessmentUploadContext, CourseAssessmentFacade } from '@app/store/course-assessment';
import { SecureUploadFacade } from '@app/store/secure-upload';
import { BtnComponent } from '../../btn/btn.component';
import { LoaderComponent } from '../../loader/loader.component';
import { ASSESSMENT_EXTENSIONS, ASSESSMENT_MAX_SIZE, ASSESSMENT_MIME_TYPES } from './upload-assessment.config';
import { BYTES_IN_MB } from '@app/shared/utils';

@Component({
    selector: 'app-upload-assessment',
    imports: [TranslatePipe, BtnComponent, CloseComponent, RouterLink, LoaderComponent, FileComponent, IconComponent],
    templateUrl: './upload-assessment.component.html',
    styleUrl: './upload-assessment.component.css',
})
export class UploadAssessmentComponent {
    private readonly courseAssessmentFacade = inject(CourseAssessmentFacade);
    private readonly secureUploadFacade = inject(SecureUploadFacade);
    private readonly uploadStatus = inject(AssessmentUploadStatusService);
    private readonly route = inject(ActivatedRoute);
    private readonly translateService = inject(TranslateService);

    private readonly queryParams = toSignal(this.route.queryParams);

    readonly courseId = computed(() => this.queryParams()?.['courseId'] ?? '');
    readonly pathId = computed(() => this.queryParams()?.['pathId'] ?? '');
    readonly nextCourseId = computed(() => this.queryParams()?.['nextCourseId'] ?? null);

    readonly loaded = computed(() => this.courseAssessmentFacade.loaded(this.courseId())());
    readonly assessment = computed(() => this.courseAssessmentFacade.assessment(this.courseId())());
    readonly pending = computed(() => this.courseAssessmentFacade.pending(this.courseId())());
    readonly submitted = computed(() => this.assessment()?.satisfied ?? false);

    readonly uploading = computed(() => this.uploadStatus.uploading(this.courseId())());
    readonly scanning = computed(() => this.uploadStatus.scanning(this.courseId())());
    readonly uploadFailed = computed(() => this.uploadStatus.failed(this.courseId())());
    readonly uploadFailedKey = computed(() => this.uploadStatus.failedKey(this.courseId())());
    readonly submitFailed = computed(() => this.uploadStatus.submitFailed(this.courseId())());

    private readonly context = computed<AssessmentUploadContext>(() => {
        const context: AssessmentUploadContext = { courseId: this.courseId() };
        const pathId = this.pathId();
        if (pathId) context.pathwayId = pathId;

        return context;
    });

    readonly acceptTypes = ASSESSMENT_MIME_TYPES;
    readonly acceptExtensions = ASSESSMENT_EXTENSIONS;
    readonly maxSize = ASSESSMENT_MAX_SIZE;

    readonly uploadHint = this.translateService.instant('assessment.upload.hint', { max: ASSESSMENT_MAX_SIZE / BYTES_IN_MB });

    constructor() {
        effect(() => {
            const courseId = this.courseId();
            if (!courseId || this.loaded() !== null) return;

            this.courseAssessmentFacade.getCourseAssessment(courseId);
        });
    }

    onFileSelected(files: File[]): void {
        const [file] = files;
        if (!file) return;

        this.secureUploadFacade.upload(this.courseId(), { uploadType: ASSESSMENT_UPLOAD_TYPE, file, context: this.context() });
    }

    retry(): void {
        this.uploadStatus.retry(this.courseId());
    }

    retrySubmit(): void {
        this.uploadStatus.retrySubmit(this.courseId());
    }
}
